/**
 * Common translations: common, help, keyboard, password, notifications, breadcrumbs, quickActions, globalSearch, ticket, validation, pageTitles, errors
 */

export const common = {
  notFoundPage: {
    title: 'Seite nicht gefunden',
    description: 'Diese Seite gibt es nicht (mehr). Vielleicht hat sich die Adresse geändert.',
    backHome: 'Zur Startseite'
  },
  // BCP-47 locale tag — read by toLocaleDateString and similar APIs that
  // need a per-locale identifier rather than the bare i18n locale code.
  locale: 'de-DE',
  save: 'Speichern',
  cancel: 'Abbrechen',
  delete: 'Löschen',
  confirm: 'Bestätigen',
  done: 'Fertig',
  edit: 'Bearbeiten',
  add: 'Hinzufügen',
  back: 'Zurück',
  next: 'Weiter',
  loading: 'Wird geladen...',
  error: 'Fehler',
  success: 'Erfolgreich',
  important: 'Wichtig',
  close: 'Schließen',
  search: 'Suchen',
  filter: 'Filtern',
  reset: 'Zurücksetzen',
  retry: 'Erneut versuchen',
  freeForever: 'Dauerhaft kostenlos',
  upgradeAnytime: 'Jederzeit auf bezahlten Tarif upgradebar.',
  yes: 'Ja',
  no: 'Nein',
  or: 'oder',
  all: 'Alle',
  none: 'Keine',
  optional: 'Optional',
  required: 'Pflichtfeld',
  active: 'Aktiv',
  creating: 'Wird erstellt...',
  settings: 'Einstellungen',
  copied: 'Kopiert',
  refresh: 'Aktualisieren',
  justNow: 'gerade eben',
  minutesAgo: 'vor {n}m',
  hoursAgo: 'vor {n}h',
  daysAgo: 'vor {n}d',
  viewAll: 'Alle anzeigen',
  termPrice: {
    period: {
      month: 'Monat',
      quarter: 'Quartal',
      halfYear: 'Halbjahr',
      year: 'Jahr'
    },
    monthlyEquivalent: 'entspricht {price} / Monat'
  }
} as const

export const help = {
  title: 'Hilfe & Kontakt',
  weHelpYou: 'Wir helfen gerne weiter.'
} as const

export const keyboard = {
  title: 'Tastenkürzel',
  categories: {
    search: 'Suche',
    navigation: 'Navigation',
    general: 'Allgemein',
    dev: 'Entwicklung'
  },
  noShortcuts: 'Keine Kürzel in dieser Kategorie',
  ctrlKNote: 'Ctrl+K öffnet ebenfalls die Suche'
} as const

export const password = {
  strength: {
    veryWeak: 'sehr schwaches',
    weak: 'schwaches',
    fair: 'ausreichendes',
    good: 'gutes',
    strong: 'starkes',
    label: '{strength} Passwort'
  },
  requirements: {
    title: 'Passwort-Anforderungen',
    minLength: 'Mindestens 8 Zeichen',
    uppercase: 'Großbuchstabe (A-Z)',
    lowercase: 'Kleinbuchstabe (a-z)',
    number: 'Zahl (0-9)'
  }
} as const

export const notifications = {
  title: 'Benachrichtigungen',
  ariaLabel: 'Benachrichtigungen',
  markAllRead: 'Alle gelesen',
  empty: 'Keine Benachrichtigungen',
  categories: {
    renewal: 'Verlängerung',
    security: 'Sicherheit',
    billing: 'Rechnung',
    system: 'System',
    verification: 'Verifizierung'
  },
  saved: 'Erfolgreich gespeichert',
  updated: 'Erfolgreich aktualisiert',
  deleted: 'Erfolgreich gelöscht',
  created: 'Erfolgreich erstellt',
  addedToCart: 'Zum Warenkorb hinzugefügt',
  copiedToClipboard: 'In Zwischenablage kopiert'
} as const

export const breadcrumbs = {
  dashboard: 'Dashboard',
  admin: 'Admin',
  email: 'E-Mail',
  domains: 'Domains',
  packet: 'Pakete',
  invoice: 'Rechnungen',
  account: 'Account',
  product: 'Produkte',
  user: 'Benutzer',
  details: 'Details',
  security: 'Sicherheit'
} as const

export const quickActions = {
  title: 'Schnellaktionen',
  ariaLabel: 'Schnellaktionen',
  newTicket: 'Neues Ticket',
  orderProduct: 'Produkt bestellen',
  domains: 'Domains',
  newProduct: 'Neues Produkt',
  adminBadge: 'Admin'
} as const

export const globalSearch = {
  triggerText: 'Suchen...',
  openSearch: 'Suche öffnen',
  openSearchShortcut: 'Suche öffnen (Strg+K)',
  placeholder: 'Suchen nach Seiten, Produkten, Domains...',
  clearInput: 'Eingabe löschen',
  closeSearch: 'Suche schließen',
  noResults: 'Keine Ergebnisse für "{query}"',
  recentSearches: 'Letzte Suchen',
  clearRecent: 'Löschen',
  hints: {
    navigate: 'navigieren',
    select: 'auswählen',
    close: 'schließen'
  },
  groups: {
    domain: 'Domain',
    navigation: 'Navigation',
    product: 'Produkte'
  },
  domainResult: {
    title: 'Domain "{query}" prüfen',
    subtitle: 'Verfügbarkeit prüfen'
  },
  nav: {
    home: 'Startseite',
    kubernetes: 'Kubernetes',
    kubernetesSub: 'Container-Orchestrierung',
    vserver: 'VServer',
    vserverSub: 'Virtuelle Server',
    webhosting: 'Webhosting',
    webhostingSub: 'Hosting-Pakete',
    domains: 'Domains',
    domainsSub: 'Domain suchen & registrieren',
    products: 'Alle Produkte',
    productsSub: 'Produktübersicht',
    cart: 'Warenkorb',
    login: 'Anmelden',
    register: 'Registrieren',
    dashboard: 'Dashboard',
    dashboardSub: 'Mein Bereich',
    impress: 'Impressum',
    privacy: 'Datenschutz',
    agb: 'AGB',
    agbSub: 'Allgemeine Geschäftsbedingungen'
  }
} as const

export const ticket = {
  title: 'Support-Ticket',
  subtitle: 'Wie können wir dir helfen?',
  responseTime: 'Unser Support-Team antwortet in der Regel innerhalb von 24 Stunden.',
  loadError: 'Das Ticket-Formular konnte nicht geladen werden. Bitte versuche es später erneut oder schreibe uns direkt an support{\'@\'}itsh.dev.'
} as const

export const a11y = {
  skipToContent: 'Zum Inhalt springen',
  mainNavigation: 'Hauptnavigation',
  bottomNavigation: 'Untere Navigation',
  shoppingCart: 'Warenkorb',
  goToCart: 'Zum Warenkorb',
  logoToHome: 'ITSH - Zur Startseite'
} as const

export const validation = {
  required: 'Pflichtfeld',
  email: 'Ungültige E-Mail-Adresse',
  emailFormat: 'Falsches E-Mail Format',
  number: 'Nur Zahlen erlaubt',
  minLength: 'Mindestens {min} Zeichen erforderlich',
  maxLength: 'Maximal {max} Zeichen erlaubt',
  phone: 'Nur Ziffern erlaubt (ohne Leerzeichen)',
  postcode: 'PLZ muss 5 Ziffern haben',
  alphanumeric: 'Nur Buchstaben, Zahlen und Satzzeichen erlaubt',
  noHtml: 'HTML-Tags sind nicht erlaubt',
  invalidInput: 'Ungültige Eingabe',
  invalidPostcodeCountry: 'Die Postleitzahl passt nicht zum Format des gewählten Landes. Bitte prüfe deine Eingabe.',
  postcodeStateMismatch: 'Die Postleitzahl liegt nicht im gewählten Bundesland. Bitte prüfe deine Eingabe.',
  ipv4: 'Ungültige IPv4-Adresse',
  ipv6: 'Ungültige IPv6-Adresse',
  hostname: 'Ungültiger Hostname',
  port: 'Muss zwischen 0 und 65535 liegen',
  numeric: 'Muss eine Zahl sein',
  emailMissingAt: "E-Mail muss ein {'@'} enthalten",
  emailMissingDomain: "Bitte Domain nach {'@'} eingeben (z.B. beispiel.de)",
  emailInvalidDomain: 'Domain muss einen Punkt enthalten (z.B. beispiel.de)',
  passwordTooShort: 'Mindestens {min} Zeichen erforderlich (aktuell: {current})',
  passwordNeedsUppercase: 'Mindestens ein Großbuchstabe (A-Z) erforderlich',
  passwordNeedsLowercase: 'Mindestens ein Kleinbuchstabe (a-z) erforderlich',
  passwordNeedsNumber: 'Mindestens eine Zahl (0-9) erforderlich',
  phoneNoLetters: 'Telefonnummer darf keine Buchstaben enthalten',
  phoneNoSpaces: 'Bitte Telefonnummer ohne Leerzeichen eingeben',
  phoneTooShort: 'Telefonnummer ist zu kurz (mindestens {min} Ziffern)',
  phoneTooLong: 'Telefonnummer ist zu lang (maximal {max} Ziffern)',
  postcodeTooShort: 'PLZ muss 5 Ziffern haben (aktuell: {current})',
  postcodeNoLetters: 'PLZ darf nur Ziffern enthalten',
  nameNoNumbers: 'Name darf keine Zahlen enthalten',
  nameInvalidChars: 'Name enthält ungültige Zeichen'
} as const

export const pageTitles = {
  // Bare page names. The 'ITSH | ' prefix is composed in the router's
  // afterEach hook: vue-i18n reads `|` as the plural delimiter, so storing
  // the full title here truncates it to 'ITSH' at render time.
  products: 'Produkte',
  kubernetes: 'Managed Kubernetes',
  objectStorage: 'Object Storage',
  webhosting: 'Webhosting',
  domainSearch: 'Domain registrieren',
  tldCatalog: 'TLD-Übersicht',
  impress: 'Impressum',
  privacy: 'Datenschutz',
  agb: 'AGB',
  sla: 'SLA',
  cancellation: 'Verträge kündigen',
  withdrawal: 'Widerrufsbelehrung',
  nis2: 'NIS2',
  pricingCalculator: 'Preisrechner',
  vserverCompare: 'VServer-Vergleich'
} as const

export const errors = {
  generic: 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.',
  serviceUnavailable:
    'Der Service ist momentan nicht verfügbar. Bitte versuche es später erneut.',
  gatewayError: 'Der Server ist vorübergehend nicht erreichbar. Bitte versuche es später erneut.',
  networkError: 'Netzwerkfehler. Bitte überprüfe deine Internetverbindung.',
  unauthorized: 'Nicht autorisiert. Bitte melde dich erneut an.',
  forbidden: 'Zugriff verweigert.',
  notFound: 'Die angeforderte Ressource wurde nicht gefunden.',
  conflict: 'Konflikt. Die Ressource existiert bereits.',
  tooManyRequests: 'Zu viele Anfragen. Bitte warte einen Moment.',
  saveFailed: 'Fehler beim Speichern',
  loadFailed: 'Fehler beim Laden',
  deleteFailed: 'Fehler beim Löschen'
} as const
